import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Form from 'Components/molecules/Form/Form';
import Input from 'Components/atoms/Input/Input';
import Button from 'Components/atoms/Button/Button';
import Label from 'Components/molecules/Label/Label';
import Link from 'Components/molecules/Link/Link';

import { withPolaris } from 'Components/protons/Polaris';
import { sendDataToDatalakeApi } from 'polaris-coreweb/exports';

import './_style.scss';

/**
 * Hashes an email address using SHA-256 after trimming and lowercasing it
 * @param {string} email - Email address to hash
 * @returns {Promise<string>} - Promise resolving to the hashed email
 */
const hashEmail = async (email) => {
  try {
    // Trim and convert email to lowercase (equivalent to LOWER(TRIM(email)))
    const normalizedEmail = email.trim().toLowerCase();

    // Check if using Node.js environment
    if (typeof window === 'undefined') {
      // Node.js environment - use dynamic import to avoid ESLint error
      const crypto = await import('crypto');
      return crypto.default.createHash('sha256').update(normalizedEmail).digest('hex');
    }

    // Browser environment
    if (!window.crypto || !window.crypto.subtle) {
      console.error('Web Crypto API not supported');
      // Fallback to a simple non-bitwise hash if crypto is not available
      let hash = 0;
      for (let i = 0; i < normalizedEmail.length; i += 1) {
        const char = normalizedEmail.charCodeAt(i);
        hash = ((hash * 31) + char) % Number.MAX_SAFE_INTEGER;
      }
      return hash.toString(16);
    }

    // Use Web Crypto API
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedEmail);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

    // Convert the hash to a hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Error hashing email:', error);
    return '';
  }
};

/**
 * NewsletterSignUpForm Component
 * A form component for users to sign up for newsletters.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.context - Context object containing the CSS prefix.
 * @param {string} props.defaultNewsletter - Default newsletter to be selected.
 * @param {Object} props.extraClassNames - Additional class names for styling.
 * @param {Object} props.newsletterOptions - Available newsletter options.
 * @param {Object} props.terms - Terms and conditions, including privacy policy URL and disclaimer.
 * @param {string} props.title - Title of the form.
 * @returns {JSX.Element} The rendered component.
 */
const NewsletterSignUpForm = ({
  brandName,
  contactUrl,
  context: { cssPrefix },
  defaultNewsletter,
  extraClassNames,
  newsletterOptions: propNewsletterOptions,
  terms: { privacyPolicyUrl, disclaimer },
  title,
}) => {
  // If the defaultNewsletter is not provided, don't render the form
  if (!defaultNewsletter) {
    return null;
  }

  // State variables for form fields and submission status
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedNewsletters, setSelectedNewsletters] = useState([]);
  const [hasConsented, setHasConsented] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  useEffect(() => {
    // Check if the 'mail' parameter exists in the query string
    const searchParams = new URLSearchParams(window.location.search);
    const emailFromQuery = searchParams.get('mail');

    // If the 'mail' parameter exists, prepopulate the email input field
    if (emailFromQuery) {
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(emailFromQuery)) {
        setEmail(emailFromQuery);
      }
    }
  }, []);

  /**
   * Default newsletter options.
   */
  const defaultNewsletterOptions = {
    auto_express: {
      label: 'Auto Express newsletter',
      frequency: '(weekly)',
      enabled: false,
    },
    auto_express_video: {
      label: 'Auto Express video newsletter',
      frequency: '(weekly)',
      enabled: false,
    },
    carbuyer: {
      label: 'CarBuyer newsletter',
      frequency: '(daily)',
      enabled: false,
    },
    evo: {
      label: 'Evo newsletter',
      frequency: '(three a week)',
      enabled: false,
    },
    driving_electric: {
      label: 'Driving Electric newsletter',
      frequency: '(daily)',
      enabled: false,
    },
  };

  /**
   * Merge default newsletter options with provided options.
   */
  const newsletterOptions = Object.keys(defaultNewsletterOptions).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        ...defaultNewsletterOptions[key],
        ...propNewsletterOptions[key],
      },
    }),
    {},
  );

  /**
   * Effect to add the default newsletter to the selected newsletters on initial render.
   * Only adds the default newsletter if it's not already included in the selection.
   */
  useEffect(() => {
    if (defaultNewsletter) {
      setSelectedNewsletters((prevSelected) => {
        if (!prevSelected.includes(defaultNewsletter)) {
          return [...prevSelected, defaultNewsletter];
        }
        return prevSelected;
      });
    }
  }, [defaultNewsletter]);

  /**
   * Handles input field changes.
   * @param {Function} setState - State setter function for the input field.
   * @returns {Function} Event handler for the input change.
   */
  const handleInputChange = (setState) => (event) => setState(event.target.value);

  /**
   * Handles checkbox changes for newsletter selection.
   * Adds the newsletter to selection if checked, removes it if unchecked.
   * @param {Object} event - The change event.
   */
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSelectedNewsletters((prevSelected) => (checked ? [...prevSelected, name] : prevSelected.filter((item) => item !== name)));
  };

  /**
   * Handles the marketing opt checkbox change.
   * @param {Object} event - The change event.
   */
  const handleConsentChange = (event) => setHasConsented(!event.target.checked);

  /**
   * Handles form submission.
   * Sends the form data to the Datalake API and resets form state on success.
   * Shows error message on failure.
   * @param {Object} event - The form submission event.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);
    setSubmissionSuccess(false);

    try {
      const hashedEmail = await hashEmail(email); // Hash the email here
      if (!hashedEmail) {
        setSubmissionError('Invalid email address.');
        return;
      }
      const data = {
        data: {
          userId: hashedEmail,
          firstName,
          lastName,
          email,
          newsletters: [...selectedNewsletters],
          marketingConsent: hasConsented,
        },
        service: 'iterable',
        timestamp: Date.now(),
      };

      // Get the environment variable dynamically here
      const datalakeProxyUrl = process.env.API_DATALAKE_PROXY_URL;
      if (!datalakeProxyUrl) {
        setSubmissionError('Configuration error. Please contact support.');
        console.error('Datalake proxy URL (API_DATALAKE_PROXY_URL) is not defined in the environment variables.');
        throw new Error('Datalake proxy URL (API_DATALAKE_PROXY_URL) is not defined in the environment variables.');
      }

      await sendDataToDatalakeApi(data, datalakeProxyUrl);

      // Push event to dataLayer on successful submission
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'newsletter_form.submit',
          'newsletter_form.newsletters': [...selectedNewsletters],
          'newsletter_form.marketingOptIn': hasConsented,
          'newsletter_form.userId': hashedEmail,
        });
      }

      setSubmissionSuccess(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setSelectedNewsletters(defaultNewsletter ? [defaultNewsletter] : []); // Re-inject default newsletter
      setHasConsented(true);
    } catch (error) {
      setSubmissionError('There was an error submitting the form. Please try again.');
      console.error('Submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentTitle = submissionSuccess ? `You’re signed up${brandName !== '' && ` with ${brandName}`}` : title;

  return (
    <div className={
      classNames({
        [`${cssPrefix}__newsletter-sign-up-form`]: true,
        ...extraClassNames,
      })
    }
    >
      <h2 className={`${cssPrefix}__newsletter-sign-up-form--title`}>{currentTitle}</h2>
      {submissionSuccess && (
        <div className={`${cssPrefix}__newsletter-sign-up-form--success`}>
          <p className={`${cssPrefix}__newsletter-sign-up-form--success--text`}>Please add us to your contacts to ensure your newsletters do not get mistaken for spam.</p>
          {contactUrl && (
            <p className={`${cssPrefix}__newsletter-sign-up-form--success--text`}>
              If you do not receive anything, please
              {' '}
              <Link href={contactUrl}>contact us</Link>
              .
            </p>
          )}
          <p className={`${cssPrefix}__newsletter-sign-up-form--success--unsubscribe-text`}>You can unsubscribe at any time using the link at the bottom of each newsletter.</p>

          <Link
            extraClassNames={{
              [`${cssPrefix}__newsletter-sign-up-form--success--button`]: true,
              '-link-as-button': true,
            }}
            href="/"
          >
            Back to homepage
          </Link>
        </div>
      )}
      {submissionError && (
        <div className={`${cssPrefix}__newsletter-sign-up-form--error`}>
          {submissionError}
        </div>
      )}
      {!submissionSuccess && (
        <Form
          onSubmit={handleSubmit}
          extraClassNames={{
            ...extraClassNames,
          }}
        >
          <div className={`${cssPrefix}__newsletter-sign-up-form--user-info`}>
            <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--name-wrapper`}>
              <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--first-name`}>
                <Label
                  content="First name*"
                  target={`${cssPrefix}__newsletter-sign-up-form--first-name`}
                />
                <Input
                  extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
                  name={`${cssPrefix}__newsletter-sign-up-form--first-name`}
                  id={`${cssPrefix}__newsletter-sign-up-form--first-name`}
                  placeholder="John"
                  required
                  type="text"
                  value={firstName}
                  onChange={handleInputChange(setFirstName)}
                />
              </div>
              <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--last-name`}>
                <Label
                  content="Last name*"
                  target={`${cssPrefix}__newsletter-sign-up-form--last-name`}
                />
                <Input
                  extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
                  name={`${cssPrefix}__newsletter-sign-up-form--last-name`}
                  id={`${cssPrefix}__newsletter-sign-up-form--last-name`}
                  placeholder="Smith"
                  required
                  type="text"
                  value={lastName}
                  onChange={handleInputChange(setLastName)}
                />
              </div>
            </div>
            <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--email`}>
              <Label
                content="Email address*"
                target={`${cssPrefix}__newsletter-sign-up-form--email`}
              />
              <Input
                extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
                name={`${cssPrefix}__newsletter-sign-up-form--email`}
                id={`${cssPrefix}__newsletter-sign-up-form--email`}
                placeholder="john.smith@example.com"
                required
                type="email"
                value={email}
                onChange={handleInputChange(setEmail)}
                isControlled
              />
            </div>
          </div>

          <div className={`${cssPrefix}__newsletter-sign-up-form--additional-newsletters`}>
            <p className={`${cssPrefix}__newsletter-sign-up-form--additional-newsletters--title`}>
              I would also like to receive the following newsletters:
            </p>
            {Object.keys(newsletterOptions)
              .filter((key) => newsletterOptions[key].enabled && key !== defaultNewsletter)
              .map((key) => {
                const { label, frequency } = newsletterOptions[key];
                return (
                  <Label
                    key={key}
                    content=""
                    extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--additional-newsletters--option`]: true }}
                    target={`${cssPrefix}__newsletter-sign-up-form--${key}`}
                  >
                    <Input
                      id={`${cssPrefix}__newsletter-sign-up-form--${key}`}
                      name={key}
                      type="checkbox"
                      checked={selectedNewsletters.includes(key)}
                      onChange={handleCheckboxChange}
                    />
                    {label}
                    {' '}
                    <span>{frequency}</span>
                  </Label>
                );
              })}
          </div>

          <div className={`${cssPrefix}__newsletter-sign-up-form--terms`}>
            <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
              You agree to the processing, storage, sharing and use of this information for
              the purpose of managing your newsletter subscription as described in our
              {' '}
              <a target="_blank" rel="noopener noreferrer" href={privacyPolicyUrl}>Privacy Policy</a>
              .
            </p>
            <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
              We believe that based on your newsletter subscription you may be interested in other related products and services we offer.
              As described in our Privacy Policy we will use the information you have shared to send you communications about such products and services.
              If you do not wish to receive these communications from us then please
              <Label
                content=""
                target={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
                extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--terms--unsubscribe`]: true }}
              >
                {' '}
                click here
                {' '}
                <Input
                  id={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
                  name={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
                  type="checkbox"
                  onChange={handleConsentChange}
                />
              </Label>
              to be unsubscribed from this activity.
            </p>
            <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
              {disclaimer}
            </p>
          </div>
          <Button
            type="submit"
            extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--button`]: true }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Sign Up'}
          </Button>
        </Form>
      )}
    </div>
  );
};

/**
 * Shape definition for newsletter option objects.
 * @type {Object}
 */
const newsletterOptionShape = PropTypes.shape({
  name: PropTypes.string,
  label: PropTypes.string,
  frequency: PropTypes.string,
  enabled: PropTypes.bool,
});

NewsletterSignUpForm.propTypes = {
  brandName: PropTypes.string,
  contactUrl: PropTypes.string,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  defaultNewsletter: PropTypes.string.isRequired,
  extraClassNames: PropTypes.shape({}),
  newsletterOptions: PropTypes.shape({
    auto_express: newsletterOptionShape,
    auto_express_video: newsletterOptionShape,
    carbuyer: newsletterOptionShape,
    evo: newsletterOptionShape,
    driving_electric: newsletterOptionShape,
  }),
  terms: PropTypes.shape({
    privacyPolicyUrl: PropTypes.string,
    disclaimer: PropTypes.string,
  }),
  title: PropTypes.string,
};

NewsletterSignUpForm.defaultProps = {
  brandName: '',
  contactUrl: '',
  context: {},
  extraClassNames: {},
  newsletterOptions: {},
  terms: {
    privacyPolicyUrl: 'https://www.carwowgroup.com/policies/privacy-policy/',
    disclaimer: '‘We’ includes AutoExpress and other Carwow Studio brands: Carbuyer, Driver Power, Driving Electric and Evo.',
  },
  title: 'We need a few details',
};

export default withPolaris(NewsletterSignUpForm);
